import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Layout from './components/Layout';
import AgeVerification from './components/AgeVerification';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import NotificationSettings from './components/NotificationSettings';
import ProfilePhotoGuard from './components/ProfilePhotoGuard';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import PhoneLogin from './pages/PhoneLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProfileSetup from './pages/ProfileSetup';
import Profile from './pages/Profile';
import PublicProfile from './pages/PublicProfile';
import BuyCredits from './pages/BuyCredits';
import RequestVerification from './pages/RequestVerification';
import VerifyEmailOTP from './pages/VerifyEmailOTP';
import AdminPanel from './pages/AdminPanel';
import Discover from './pages/Discover';
import Feed from './pages/Feed';
import FemaleDashboard from './pages/FemaleDashboard';
import Inbox from './pages/Inbox';
import Chat from './pages/Chat';
import ActiveCallScreen from './pages/ActiveCallScreen';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import RedeemVoucher from './pages/RedeemVoucher';
import CreatorDashboard from './pages/CreatorDashboard';
import CreatorProfile from './pages/CreatorProfile';
import Notifications from './pages/Notifications';
import MySubscriptions from './pages/MySubscriptions';
import EarlyPayout from './pages/EarlyPayout';
import ContactSupport from './pages/ContactSupport';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import './App.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID.apps.googleusercontent.com';

const PrivateRoute = ({ children, useLayout = true }) => {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" />;
  return useLayout ? <Layout>{children}</Layout> : children;
};

function App() {
  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/service-worker.js')
          .then((registration) => {
            console.log('SW registered:', registration);
          })
          .catch((error) => {
            console.log('SW registration failed:', error);
          });
      });
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <ToastProvider>
        <AgeVerification />
        <PWAInstallPrompt />
        {/* <NotificationSettings /> - Disabled: unprofessional appearance */}
        <Router>
          <ProfilePhotoGuard>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/phone-login" element={<PhoneLogin />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<PrivateRoute useLayout={false}><VerifyEmailOTP /></PrivateRoute>} />
              <Route path="/profile-setup" element={<PrivateRoute useLayout={false}><ProfileSetup /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/profile/:userId" element={<PrivateRoute><PublicProfile /></PrivateRoute>} />
              <Route path="/buy-credits" element={<PrivateRoute><BuyCredits /></PrivateRoute>} />
              <Route path="/creator-dashboard" element={<PrivateRoute><CreatorDashboard /></PrivateRoute>} />
              <Route path="/creator/:userId" element={<PrivateRoute><CreatorProfile /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
              <Route path="/my-subscriptions" element={<PrivateRoute><MySubscriptions /></PrivateRoute>} />
              <Route path="/early-payout" element={<PrivateRoute><EarlyPayout /></PrivateRoute>} />
              <Route path="/request-verification" element={<PrivateRoute useLayout={false}><RequestVerification /></PrivateRoute>} />
              <Route path="/admin" element={<PrivateRoute useLayout={false}><AdminPanel /></PrivateRoute>} />
              <Route path="/feed" element={<PrivateRoute><Feed /></PrivateRoute>} />
              <Route path="/discover" element={<PrivateRoute><Discover /></PrivateRoute>} />
              <Route path="/dashboard" element={<PrivateRoute><FemaleDashboard /></PrivateRoute>} />
              <Route path="/inbox" element={<PrivateRoute><Inbox /></PrivateRoute>} />
              <Route path="/chat/:matchId" element={<PrivateRoute><Chat /></PrivateRoute>} />
              <Route path="/call/:callId" element={<PrivateRoute useLayout={false}><ActiveCallScreen /></PrivateRoute>} />
              <Route path="/contact-support" element={<PrivateRoute><ContactSupport /></PrivateRoute>} />
              <Route path="/redeem-voucher" element={<PrivateRoute useLayout={false}><RedeemVoucher /></PrivateRoute>} />
              <Route path="/payment/success" element={<PrivateRoute useLayout={false}><PaymentSuccess /></PrivateRoute>} />
              <Route path="/payment/cancel" element={<PrivateRoute useLayout={false}><PaymentCancel /></PrivateRoute>} />
              
              {/* Public Pages */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              
              {/* Redirect old matches route to discover */}
              <Route path="/matches" element={<Navigate to="/discover" replace />} />
              {/* Catch all - redirect to discover */}
              <Route path="*" element={<Navigate to="/discover" replace />} />
            </Routes>
          </ProfilePhotoGuard>
        </Router>
        </ToastProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
